import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

type SiteHeaderProps = {
  transparentOnTop?: boolean;
};

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/topics', label: '话题合集', end: false },
  { to: '/about', label: '关于我们', end: false },
];

export default function SiteHeader({ transparentOnTop = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !transparentOnTop || scrolled;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:h-[72px] md:px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md ${
              solid ? 'bg-accent-500 text-background-50' : 'bg-background-50 text-accent-600'
            }`}
          >
            <i className="ri-flashlight-fill text-lg" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-wide ${
                solid ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              看料大瓜
            </span>
            <span className={`mt-0.5 text-[10px] tracking-[0.2em] ${solid ? 'text-foreground-500' : 'text-background-200'}`}>
              GUATIAN DIGEST
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive
                    ? solid
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-background-50/20 text-background-50'
                    : solid
                      ? 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                      : 'text-background-100 hover:text-background-50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <Link
            to="/topics"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            开始补瓜
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          aria-expanded={menuOpen}
          className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
            solid ? 'bg-background-100 text-foreground-900' : 'bg-background-50/20 text-background-50'
          }`}
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-3-line text-xl'} aria-hidden="true"></i>
        </button>
      </nav>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col px-4 py-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-sm ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/topics"
              className="mt-2 rounded-md bg-primary-500 px-4 py-3 text-center text-sm font-medium text-background-50"
            >
              开始补瓜
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}